/**
 * IndicatorTileView - Pure presentation component for a single indicator tile
 *
 * Displays a key metric value with label in a clickable card format.
 * Three layout variants:
 * - Compact: inline chip with label + value in an HStack (used in modal context)
 * - Mobile: horizontal layout with label-left, value-right, flex to fill row
 * - Desktop: centered vertical layout with fixed width
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import { ArrowRight } from "lucide-react-native";
import { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Text } from "@/components/atoms/Text";

import type { IndicatorTileViewProps } from "@/features/player-detail/utils/playerPageHeaderTypes";

/**
 * Get dynamic font size class based on value length to prevent overflow
 * @param value - The value string to measure
 * @returns Tailwind font size class
 */
const getValueFontClass = (value: string): string =>
  value.length > 12
    ? "text-title"
    : value.length > 6
      ? "text-title-lg"
      : "text-display-sm";

/**
 * Get mobile-specific font size class for horizontal layout
 * @param value - The value string to measure
 * @returns Tailwind font size class for mobile
 */
const getMobileValueFontClass = (value: string): string =>
  value.length > 12
    ? "text-body"
    : value.length > 6
      ? "text-title-sm"
      : "text-title";

// design-system: dynamic-style waiver — data-driven valueColor prop; passed as a
// single style reference so no inline object literal renders on a JSX element.
const colorStyle = (color: string) => ({ color });

/** Layout variant key derived from props */
type TileVariant = "compact" | "fullWidth" | "smallScreen" | "desktop";

/**
 * Pressable className per layout variant.
 *
 * SE-5446: compact (modal) tiles and smallScreen tiles flex:1 (prod parity) so
 * the rail spans the sheet width instead of huddling in the center.
 *
 * Desktop tiles flex-grow to fill the rail row but hold a 160px legibility
 * floor (min-w-40 = 160px exactly) so the five-tile rail wraps to additional
 * rows on narrow desktop widths instead of overflowing horizontally (SE-4616).
 * flex-grow already sizes the tiles to the V1.6 Figma width (~245px) when the
 * row has room, so SE-5372 keeps the 160px floor — raising it forces a
 * premature wrap and breaks the SE-4616 "five tiles on one row at 1440px"
 * invariant. The min-width lives on the Pressable (the rail's flex child) — not
 * the Box — so flex-wrap on the rail can size and break the row correctly.
 */
const TILE_CLASSES: Record<TileVariant, string> = {
  compact: "flex-1 min-w-0",
  fullWidth: "w-full",
  smallScreen: "flex-1 min-w-0",
  desktop: "flex-1 min-w-40",
};

/**
 * Determine which layout variant to use from boolean props
 * @param isCompact - Whether compact chip layout is active
 * @param isFullWidth - Whether the tile stretches to full width
 * @param isSmallScreen - Whether mobile layout is active
 * @returns The resolved tile variant key
 */
function getTileVariant(
  isCompact: boolean,
  isFullWidth: boolean,
  isSmallScreen: boolean
): TileVariant {
  if (isCompact) return "compact";
  if (isFullWidth) return "fullWidth";
  if (isSmallScreen) return "smallScreen";
  return "desktop";
}

/**
 * Resolve theme key from isDark boolean
 * @param isDark - Whether dark mode is active
 * @returns Theme key for lookup tables
 */
const toThemeKey = (isDark: boolean): "dark" | "light" =>
  isDark ? "dark" : "light";

/**
 * Append opacity class when pressed
 * @param pressed - Whether the tile is currently pressed AND navigable. Callers
 * gate on the press handler because a pressable keeps reporting pressed state
 * even without one, and a tile that goes nowhere should give no press feedback.
 * @returns Opacity class suffix or empty string
 */
const pressedSuffix = (pressed: boolean): string =>
  pressed ? " opacity-60" : "";

/** Label text color class by theme */
const LABEL_COLOR: Record<"dark" | "light", string> = {
  dark: "text-content-subtle",
  light: "text-content-muted",
};

/**
 * Standard tile border+bg class (static — semantic tokens auto-invert by
 * theme). SE-5446: the V1.6 stat card uses a light-gray fill
 * (#f6f6f6 → background-50) with a #dcdbdb hairline border (→ outline-200) —
 * reverting the SE-5372 white fill (background-0 / outline-300) that no
 * longer matches the reference.
 */
const STANDARD_THEME_CLASS = "border-outline-default bg-surface-card";

/**
 * Standard tile padding class by screen variant.
 * @remarks The desktop tile no longer pins a fixed width — that forced the
 * five-tile rail to overflow horizontally on narrow desktop widths. The
 * flex-grow + 160px minWidth live on the Pressable (styles.desktopTile) so the
 * rail can wrap to additional rows (SE-4616); the length-based value font
 * scaling (getValueFontClass) keeps the value legible down to the 160px floor.
 * SE-5446: h-full so the bordered card fills the stretched rail cell — all 5
 * tiles share the tallest card's height instead of hugging their content.
 */
const STANDARD_SIZE: Record<"smallScreen" | "desktop", string> = {
  smallScreen: "px-3 py-3",
  desktop: "h-full px-4 py-3",
};

/**
 * Hover-state additions for the arrow variant: subtle background fill + pointer
 * cursor on web (static — semantic tokens auto-invert by theme). NativeWind
 * `web:` prefix gates to web platforms; native mobile inherits no hover (no
 * hover events on touch). Per SE-5024 Figma: Background/background muted
 * #e7e8e7 → bg-surface-subtle.
 */
const ARROW_HOVER_CLASS = "web:hover:bg-surface-subtle web:cursor-pointer";

/**
 * Resolve the hover class for the arrow variant; returns an empty string when
 * the arrow should not render so the className interpolation stays clean.
 * @param show - Whether the arrow variant is active
 */
const getArrowHoverClass = (show: boolean): string =>
  show ? ARROW_HOVER_CLASS : "";

/**
 * Whether to render the routed-breakdown right-arrow + hover fill. Shown on
 * both desktop and mobile standard tiles (SE-5084 promotes the affordance to
 * the mobile primary KPI tiles); only the compact (modal chip) layout
 * suppresses it since chips have no room for the indicator.
 * @param showArrow - Whether the caller opted into the arrow affordance
 * @param isCompact - Compact chip layout flag
 */
const shouldShowArrow = (showArrow: boolean, isCompact: boolean): boolean =>
  showArrow && !isCompact;

/**
 * Pressed-state class suffix, gated on the tile actually being interactive.
 * A pressable still tracks its pressed state without a handler, so the
 * SE-5446 intent — no press feedback on non-navigable tiles like Months
 * Left — lives in this gate (SE-6968).
 * @param pressed - Pressable's live pressed state
 * @param onPress - The tile's press handler, absent on non-navigable tiles
 */
const interactivePressedSuffix = (
  pressed: boolean,
  onPress?: () => void
): string => pressedSuffix(pressed && !!onPress);

/**
 * IndicatorTileView renders a clickable metric tile with value and label
 * @param root0 - View props including value, label, and press handler
 * @param root0.label - Display label shown below (desktop) or left of (mobile) the value
 * @param root0.value - Formatted value string to display prominently
 * @param root0.valueColor - CSS color string for the value text
 * @param root0.onPress - Callback invoked when the tile is pressed
 * @param root0.testID - Optional test identifier for E2E testing
 * @param root0.isDark - Whether dark theme is active, affects tile styling
 * @param root0.isSmallScreen - Whether to use mobile horizontal layout
 * @param root0.isFullWidth - Whether the tile should stretch to full width (no flex shrink)
 * @param root0.isCompact - Whether to render as an inline chip (used in modal context)
 * @param root0.showArrow - Whether to render the routed-breakdown arrow + hover fill (desktop only)
 * @returns Pressable tile component
 */
const IndicatorTileView = ({
  label,
  value,
  valueColor,
  onPress,
  testID,
  isDark,
  isSmallScreen = false,
  isFullWidth = false,
  isCompact = false,
  showArrow = false,
}: IndicatorTileViewProps) => (
  <Pressable
    onPress={onPress}
    // SE-6968: do NOT mark handler-less tiles as `disabled`. The vendored
    // Gluestack pressable primitive carries a base class that drops any node in
    // the disabled data-state to 40% opacity, which ghosted the whole Months
    // Left card — border, label and value alike — since that tile is the one
    // built without a routed breakdown. Non-interactivity is expressed instead
    // as: no press handler, focusable={false} (a pressable is a keyboard tab
    // stop by default on web), and the gated pressed suffix below. A pressable
    // still tracks its pressed state without a handler, so the SE-5446 intent —
    // no press feedback on non-navigable tiles — lives in that gate.
    focusable={!!onPress}
    testID={testID}
    accessibilityRole={onPress ? "button" : undefined}
    accessibilityLabel={`${label}: ${value}`}
    className={
      TILE_CLASSES[getTileVariant(isCompact, isFullWidth, isSmallScreen)]
    }
  >
    {({ pressed }) =>
      isCompact ? (
        // SE-5446: the modal stat cards mirror the full-page card (Raniel's V1.6
        // reference) — value on top, label beneath, gray fill (STANDARD_THEME_CLASS) —
        // instead of the SE-5420 horizontal label+value chip. flex:1
        // (TILE_CLASSES.compact) makes the rail span the sheet edge-to-edge.
        <Box
          className={`h-full rounded-md border px-4 py-3 ${STANDARD_THEME_CLASS} ${onPress ? "" : "web:cursor-default"}${interactivePressedSuffix(pressed, onPress)}`}
        >
          <Stack className="items-center gap-1">
            {/* design-system: dynamic-style waiver — data-driven value (valueColor prop) */}
            <Text
              variant="unstyled"
              className={`whitespace-nowrap text-center font-heading ${getValueFontClass(value)}`}
              // eslint-disable-next-line no-restricted-syntax -- data-driven valueColor (free CSS color prop); no semantic token
              UNSAFE_style={colorStyle(valueColor)}
            >
              {value}
            </Text>
            <Text
              variant="caption"
              className={`whitespace-nowrap text-center ${LABEL_COLOR[toThemeKey(isDark)]}`}
            >
              {label}
            </Text>
          </Stack>
        </Box>
      ) : (
        <Box
          className={`rounded-md border ${STANDARD_SIZE[isSmallScreen ? "smallScreen" : "desktop"]} ${STANDARD_THEME_CLASS} ${getArrowHoverClass(shouldShowArrow(showArrow, isCompact))} ${onPress ? "" : "web:cursor-default"}${interactivePressedSuffix(pressed, onPress)}`}
        >
          {/* SE-5372 / V1.6 Figma: the breakdown affordance is an inline
              ArrowRight (→) immediately after the label — centred on desktop,
              trailing the value on mobile — not a top-right corner chevron. */}
          {isSmallScreen ? (
            <Stack
              direction="horizontal"
              className="items-center justify-between"
            >
              <Text variant="body" className={LABEL_COLOR[toThemeKey(isDark)]}>
                {label}
              </Text>
              <Stack direction="horizontal" className="items-center gap-1">
                {/* design-system: dynamic-style waiver — data-driven value (valueColor prop) */}
                <Text
                  variant="unstyled"
                  className={`font-heading ${getMobileValueFontClass(value)}`}
                  // eslint-disable-next-line no-restricted-syntax -- data-driven valueColor (free CSS color prop); no semantic token
                  UNSAFE_style={colorStyle(valueColor)}
                >
                  {value}
                </Text>
                {shouldShowArrow(showArrow, isCompact) ? (
                  <Icon
                    as={ArrowRight}
                    size="2xs"
                    testID="indicator-tile-arrow"
                    className="text-content-secondary"
                  />
                ) : null}
              </Stack>
            </Stack>
          ) : (
            <Stack className="items-center gap-1">
              {/* design-system: dynamic-style waiver — data-driven value (valueColor prop) */}
              <Text
                variant="unstyled"
                className={`whitespace-nowrap text-center font-heading ${getValueFontClass(value)}`}
                // eslint-disable-next-line no-restricted-syntax -- data-driven valueColor (free CSS color prop); no semantic token
                UNSAFE_style={colorStyle(valueColor)}
              >
                {value}
              </Text>
              <Stack
                direction="horizontal"
                className="items-center justify-center gap-1"
              >
                <Text
                  variant="caption"
                  className={`whitespace-nowrap text-center ${LABEL_COLOR[toThemeKey(isDark)]}`}
                >
                  {label}
                </Text>
                {shouldShowArrow(showArrow, isCompact) ? (
                  <Icon
                    as={ArrowRight}
                    size="2xs"
                    testID="indicator-tile-arrow"
                    className="text-content-secondary"
                  />
                ) : null}
              </Stack>
            </Stack>
          )}
        </Box>
      )
    }
  </Pressable>
);

IndicatorTileView.displayName = "IndicatorTileView";

export default memo(IndicatorTileView);
